import { useEffect, useRef } from "react";
import {
	Box,
	Group,
	Image,
	Stack,
	Text,
	TextInput,
	UnstyledButton,
} from "@mantine/core";
import { useLocation, useNavigate } from "react-router-dom";

import { useLocale } from "@/apps/@components";
import { findWallpaper } from "../wallpapers";
import { useWallpaperFlow } from "./WallpaperFlowContext";
import { CampaignButton } from "../components";
import { logWallpaperEvent, logWallpaperPage } from "../wallpaperLog";
import { getStoredFrom } from "../wallpaperParams";

/** 字色選項：實心圓＝選中、空心圓＝未選，選中文字加底線。 */
const COLOR_OPTIONS = [
	{ value: "black", labelKey: "text.color.black" },
	{ value: "white", labelKey: "text.color.white" },
] as const;

/** 字型 → 合圖用的字體、字級、字重。 */
const FONT_STYLE = {
	classic: {
		fontFamily: "'SweetSansPro', sans-serif",
		fontSize: "6.1vw",
		fontWeight: 700,
	},
	handwrite: {
		fontFamily: "'WorkhorseScript', cursive",
		fontSize: "5.4vw",
		fontWeight: 100,    
	},
} as const;

const TEXT_COLOR = { black: "#000000", white: "#FFFFFF" } as const;

const SHADOW_COLOR = { black: "rgba(255, 255, 255, 0.9)", white: "rgba(0, 0, 0, 0.9)" } as const;

const SHADOW_OFFSET_RATIO = 4 / 1080;
const SHADOW_BLUR_RATIO = 10 / 1080;
const PREVIEW_WIDTH_VW = 36.1;
const TEXT_SHADOW = `0 ${(PREVIEW_WIDTH_VW * SHADOW_OFFSET_RATIO).toFixed(3)}vw ${(
	PREVIEW_WIDTH_VW * SHADOW_BLUR_RATIO
).toFixed(3)}vw`;

/** 只允許英文、最多 4 字元。 */
const MAX_LEN = 4;
const sanitize = (v: string) => v.replace(/[^A-Za-z]/g, "").slice(0, MAX_LEN);

function getScrollParent(node: HTMLElement | null): HTMLElement | null {
	let el = node?.parentElement ?? null;
	while (el) {
		const overflowY = getComputedStyle(el).overflowY;
		if (
			(overflowY === "auto" || overflowY === "scroll") &&
			el.scrollHeight > el.clientHeight
		) {
			return el;
		}
		el = el.parentElement;
	}
	return null;
}

export default function WallpaperTextSettingPage() {
	const { t } = useLocale();
	const navigate = useNavigate();
	const { search } = useLocation();
	const { wallpaperId, font, color, text, update } = useWallpaperFlow();
	const wallpaper = findWallpaper(wallpaperId);
	const updateRef = useRef(update);
	updateRef.current = update;

	const { locale } = useLocale();
	const langSuffix = locale === "en" ? "?lang=en" : "";

	useEffect(() => {
		if (!wallpaper) {
			navigate({ pathname: "/wallpaper/start", search }, { replace: true });
		}
	}, [wallpaper, navigate, search]);

	// 離開此頁（回上一頁、進下一頁、或離開活動）時清空已輸入文字
	useEffect(() => {
		logWallpaperPage({
			path: `/wallpaper/text${langSuffix}`,
			from: getStoredFrom(),
		});
		return () => {
			updateRef.current({ text: "" });
		};
	}, []);

	const handlePrev = () => {
		logWallpaperEvent({
			category: "cta",
			path: `/wallpaper/text${langSuffix}`,
			action: "prev",
			event: "cta_text_prev",
		});
		navigate({ pathname: "/wallpaper/start", search });
	};

	// 帶著合成參數快照進完成頁（text 會在本頁卸載時被清空，故用 router state 傳遞）
	const handleNext = () => {
		if (!text) return;
		logWallpaperEvent({
			category: "cta",
			path: `/wallpaper/text${langSuffix}`,
			action: "confirm_text",
			event: "cta_text_next",
		});
		navigate(
			{ pathname: "/wallpaper/download", search },
			{ state: { wallpaperId, text, font, color } },
		);
	};

	if (!wallpaper) return null;

	return (
		<Stack gap={0} align="center" w="100%" mt="1.5vw" pb="8vw">
			<Text
				size={locale === "en" ? "4vw" : "4.5vw"}
				c="#433931"
				fw={500}
				lh="6.3vw"
				ta="center"
				style={{
					whiteSpace: "pre-wrap",
					letterSpacing: locale === "en" ? "-0.03em" : "0.1em",
				}}
			>
				{t("text.title")}
			</Text>
			{locale === "en" && (
				<Text
					size="3.5vw"
					c="#433931"
					fw={500}
					lh="6.3vw"
					ta="center"
					mt="1.2vw"
					style={{
						whiteSpace: "pre-wrap",
						letterSpacing: "-0.03em",
					}}
				>
					{t("text.title.en")}
				</Text>
			)}

			{/* 合圖預覽：文字即時疊在桌布圖上 */}
			<Box pos="relative" w="36.1vw" mt="3.5vw" style={{ flexShrink: 0 }}>
				<Image
					src={wallpaper.src}
					alt={`wallpaper-${wallpaper.id}`}
					w="100%"
					h="78.1vw"
					fit="cover"
				/>
				{text && (
					<Text
						style={{
							position: "absolute",
							top: wallpaper.textPos.top,
							left: wallpaper.textPos.left,
							transform: "translate(-50%, -50%)",
							fontFamily: FONT_STYLE[font].fontFamily,
							fontSize: FONT_STYLE[font].fontSize,
							fontWeight: FONT_STYLE[font].fontWeight,
							color: TEXT_COLOR[color],
							textShadow: `${TEXT_SHADOW} ${SHADOW_COLOR[color]}`,
							lineHeight: 1,
							whiteSpace: "nowrap",
							pointerEvents: "none",
						}}
					>
						{text}
					</Text>
				)}
			</Box>

			<Stack w="85vw" gap={0} py="4.5vw">
				<TextInput
					label={t("text.inputLabel")}
					radius={0}
					value={text}
					onChange={(e) => update({ text: sanitize(e.currentTarget.value) })}
					onBlur={(e) => {
						const scrollParent = getScrollParent(e.currentTarget);
						setTimeout(() => {
							scrollParent?.scrollTo({ top: 0, behavior: "smooth" });
							document.body.scrollTo({ top: 0, behavior: "smooth" });
							document.documentElement.scrollTo({ top: 0, behavior: "smooth" });
							window.scrollTo({ top: 0, behavior: "smooth" });
						}, 150);
					}}
					maxLength={MAX_LEN}
					styles={{
						label: {
							fontSize: locale === "en" ? "3.3vw" : "4vw",
							color: "#433931",
							letterSpacing: locale === "en" ? "0.02em" : "0.1em",
						},
						input: {
							height: "8.6vw",
							minHeight: "8.6vw",
							fontSize: "4vw",
							color: "#433931",
							marginTop: "1vw",
							textAlign: "center",
							lineHeight: "8.6vw",
							paddingTop: 0,
							paddingBottom: 0,
							border: "0.5px solid #433931",
							backgroundColor: "transparent",
						},
					}}
				/>

				<Stack gap="1vw" mt="4vw">
					<Text
						c="#433931"
						size={locale === "en" ? "3.3vw" : "4vw"}
						fw={500}
						style={{ letterSpacing: locale === "en" ? "0.02em" : "0.1em" }}
					>
						{t("text.fontLabel")}
					</Text>
					<Group grow gap="7.1vw" wrap="nowrap">
						<CampaignButton
							variant="outline"
							active={font === "classic"}
							onClick={() => update({ font: "classic" })}
							fz={locale === "en" ? "3.3vw" : "4vw"}
							style={{ letterSpacing: locale === "en" ? "0.02em" : "0.2em" }}
							h="8.9vw"
							lh="normal"
						>
							{t("text.font.classic")}
						</CampaignButton>
						<CampaignButton
							variant="outline"
							active={font === "handwrite"}
							onClick={() => update({ font: "handwrite" })}
							fz={locale === "en" ? "3.3vw" : "4vw"}
							style={{ letterSpacing: locale === "en" ? "0.02em" : "0.2em" }}
							h="8.9vw"
							lh="normal"
						>
							{t("text.font.handwrite")}
						</CampaignButton>
					</Group>
				</Stack>

				<Group gap="17.8vw" mt="6.3vw" align="center" wrap="nowrap">
					<Text
						c="#433931"
						size={locale === "en" ? "3.3vw" : "4vw"}
						fw={500}
						style={{ letterSpacing: locale === "en" ? "0.02em" : "0.1em" }}
					>
						{t("text.colorLabel")}
					</Text>
					<Group gap={locale === "en" ? "12vw" : "16vw"}>
						{COLOR_OPTIONS.map((opt) => {
							const selected = color === opt.value;
							return (
								<UnstyledButton
									key={opt.value}
									onClick={() => update({ color: opt.value })}
								>
									<Group gap="2vw" align="center" wrap="nowrap">
										<Box
											w="3.8vw"
											h="3.8vw"
											style={{
												borderRadius: "50%",
												border: "0.5px solid #433931",
												backgroundColor: selected ? "#433931" : "transparent",
												flexShrink: 0,
											}}
										/>
										<Text
											c="#433931"
											fw={500}
											size={locale === "en" ? "3vw" : "4vw"}
											style={{
												textDecoration: selected ? "underline" : "none",
												textUnderlineOffset: "0.8vw",
                        letterSpacing: locale === "en" ? "0.08em" : "0.2em"
											}}
										>
											{t(opt.labelKey)}
										</Text>
									</Group>
								</UnstyledButton>
							);
						})}
					</Group>
				</Group>

				<Group grow gap="7.1vw" mt="5vw" wrap="nowrap">
					<CampaignButton
						variant="outline"
						onClick={handlePrev}
						fz={locale === "en" ? "3.3vw" : "4vw"}
						style={{ letterSpacing: locale === "en" ? "0.02em" : "0.2em" }}
						lh="normal"
					>
						{t("text.btn.prev")}
					</CampaignButton>
					<CampaignButton
						variant="outline"
						disabled={!text}
						onClick={handleNext}
						fz={locale === "en" ? "3.3vw" : "4vw"}
						style={{ letterSpacing: locale === "en" ? "0.02em" : "0.2em" }}
						lh="normal"
					>
						{t("text.btn.next")}
					</CampaignButton>
				</Group>
			</Stack>
		</Stack>
	);
}
